import {Box, Dialog, DialogContent, Grid, TextField, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {makeStyles} from "@mui/styles";
import {useForm} from "react-hook-form";
import {getStoredSession} from "../../LocalStorageService";
import {getFormattedProperty, isArrayOfStrings, isString, truncateList, stringifyPreferences} from "../../utils/utils";
import {addressValidationRules, cityValidationRules, companyNameValidationRules, contactNameValidationRules, countryValidationRules, descriptionMessageValidationRules, emailValidationRules, nationalPhoneNumberValidationRules, postalCodeValidationRules, stateValidationRules, urgencyLevelValidationRules} from "../../utils/validationRules";
import {addContact} from "../../utils/apiCalls";
import {ValidatedAdornedField, ValidatedDialledNumberField} from "../../utils/formsUtils";
import {StyledButtonContained, StyledButtonOutlined, disabledInputGreySx} from "../../utils/uiUtils";

/*
 * The optional address block on the Support Request. Named here because two places have to agree on what
 * "the operator typed an address" means: the rules that decide which parts are required, and the payload
 * that decides whether an address is sent at all.
 */
const ADDRESS_FIELD_NAMES = ["relatedAddress1", "postalCode", "city", "state", "country"];
// Takes a lookup rather than an object so the same answer serves both callers: watch(name) while rendering,
// data[name] on submit.
const isAddressStarted = (get) => ADDRESS_FIELD_NAMES.some(name => get(name)?.trim());

/**
 * ContactForm — contact form Dialog for restore, destruction, or help requests.
 *
 * @param {boolean} isFormOpen - Controls dialog visibility.
 * @param {function} closeForm - Closes the form dialog.
 * @param {object} actionRelatedData - Data for the action. Shape depends on contactType:
 *   restore: { addedRows, dialogInitiator, actionPreferences }
 *   destruction: { addedRows, actionPreferences }
 *   help: undefined
 *   Legacy restore shape also supported: { cloudShow, physicalShow, addedRows, dialogInitiator, checkoutRestorePreferences }
 * @param {function} onSuccessfulSubmit - Callback on successful submission.
 * @param {string} contactType - "restore", "destruction", or "help".
 */
/** The three levels every request offers, lowest first. */
const BASE_URGENCY_LEVELS = [
    {value: 'low', label: 'Low'},
    {value: 'medium', label: 'Medium'},
    {value: 'high', label: 'High'},
];

/**
 * Where Severe is offered, which is Data Management's Restore and Destruction requests and nowhere else.
 *
 * An allow-list rather than `contactType !== 'help'` so that a contact type added later has to opt in: what
 * Severe does on the way out is send an SMS (tape/signals.py, on urgency == SEVERE) as well as the usual
 * email, and a new form inheriting a pager escalation by default is not a mistake worth leaving available.
 *
 * The header's Support Request stays on the three base levels. `severe` has never stopped being valid — it
 * is in the API's CONTACT_URGENCY choices and in urgencyLevelValidationRules — so this is only about which
 * forms let a reader pick it.
 */
const SEVERE_URGENCY_CONTACT_TYPES = ['restore', 'destruction'];

const ContactForm = ({
                         isFormOpen,
                         closeForm,
                         actionRelatedData = undefined,
                         restoreRelatedData = undefined,
                         onSuccessfulSubmit = () => {},
                         contactType,
                         openSnackbar = () => {}
                     }) => {
    // Support legacy prop name
    const relatedData = actionRelatedData || restoreRelatedData;

    // Severe last, so the list still reads lowest to highest — the opposite order to the API's choices tuple.
    const urgencyLevels = SEVERE_URGENCY_CONTACT_TYPES.includes(contactType)
        ? [...BASE_URGENCY_LEVELS, {value: 'severe', label: 'Severe'}]
        : BASE_URGENCY_LEVELS;

    const [isDefaultLoaded, setIsDefaultLoaded] = useState(false);
    const [closeButtonText, setCloseButtonText] = useState("Close");

    // Read from the session rather than from the form, so an admin typing into an empty Company Name does not
    // lock the box under their own keystrokes.
    const sessionCompanyName = getStoredSession()?.user?.company_name ?? "";

    // Action related states
    const [concatenatedOptions, setConcatenatedOptions] = useState("");
    const [concatenatedFileNames, setConcatenatedFileNames] = useState("");
    const [concatenatedVolsers, setConcatenatedVolsers] = useState("");
    const [fullTapeIds, setFullTapeIds] = useState("");
    const [actionPreferences, setActionPreferences] = useState({});
    const [actionFileDetails, setActionFileDetails] = useState({});

    // Destruction confirmation
    const [confirmationText, setConfirmationText] = useState("");
    const tapeCount = relatedData?.addedRows?.length || 0;
    const expectedConfirmation = `request destruction of ${tapeCount} ${tapeCount === 1 ? "tape" : "tapes"}`;
    const isConfirmationValid = contactType !== "destruction" || confirmationText === expectedConfirmation;

    function getDefaultValues() {
        const localCopyUserDetails = getStoredSession();
        const user = localCopyUserDetails?.user || {};

        return {
            companyName: user.company_name ?? "",
            contactName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            emailAddress: user.email ?? "",
            /*
             * The field asks for a mobile now, so the stored mobile is what it opens on, falling back to the
             * phone number for the users saved before nexus-api kept a mobile at all. Whichever it is, it
             * arrives as one string and the two boxes split it — a number stored without a country code shows
             * whole and is refused with the correction, rather than being silently assumed Australian.
             */
            mobileNumber: user.mobile_number || user.phone_number || "",
            fileNames: concatenatedFileNames ?? "",
            tapeVolsers: concatenatedVolsers ?? "",
            additionalInstructions: "",
            options: concatenatedOptions ?? "",
            urgencyLevel: "low",

            relatedAddress1: "",
            postalCode: "",
            city: "",
            state: "",
            country: "",
        };
    }

    const {
        register,
        handleSubmit,
        watch,
        formState: {errors, isValid},
        setValue,
        control,
        getValues,
        reset,
        trigger
    } = useForm({
        defaultValues: {...getDefaultValues()},
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    // Whether the operator has begun the optional address. Watched, so the stars appear and disappear with
    // the first and last character typed into any of the five boxes.
    const addressStarted = isAddressStarted((name) => watch(name));
    /*
     * Whether the address is begun but unfinished. Submit needs its own answer to that rather than leaning on
     * isValid: the stars turning on does not make the form invalid by itself, because react-hook-form
     * re-registers the five boxes with their new rules in an effect, after the keystroke that turned the stars
     * on has already been validated under the old ones. Submit would stay enabled over four empty required
     * boxes and the operator would meet the same 400 the stars exist to prevent. The required rules still
     * stand — they are what names the box in a helper text once it is left — this only keeps the button
     * honest in the meantime.
     */
    const addressIncomplete = addressStarted && !ADDRESS_FIELD_NAMES.every(name => watch(name)?.trim());

    // ─── Data extraction per contactType ──────────────────────────────────────
    useEffect(() => {
        if (contactType === "restore") {
            if (!relatedData) throw new Error("actionRelatedData is not defined");
            if (!relatedData.hasOwnProperty("addedRows") || !relatedData.hasOwnProperty("dialogInitiator")) {
                throw new Error("actionRelatedData must contain addedRows and dialogInitiator properties");
            }

            // Build volsers list
            let volserList = [];
            let fileNameList = [];

            if (relatedData.dialogInitiator === 'itemList') {
                fileNameList = relatedData.addedRows?.map((item) => getFormattedProperty(item, "name", "str")) || [];
                volserList = relatedData.addedRows?.map((item) => getFormattedProperty(item, "volsers", "str")) || [];
            }

            if (relatedData.dialogInitiator === 'tapeList') {
                volserList = relatedData.addedRows?.map((item) => getFormattedProperty(item, "item_volser", "str")) || [];
            }

            // Deduplicate only non-empty volsers, preserve empty count
            const validVolsers = [...new Set(volserList.filter(v => v && v.trim()))];
            const emptyVolserCount = volserList.filter(v => !v || !v.trim()).length;
            const volsersWithEmpties = [...validVolsers, ...Array(emptyVolserCount).fill("")];
            setConcatenatedVolsers(truncateList(volsersWithEmpties, 80, "volser"));
            setConcatenatedFileNames(fileNameList.length > 0 ? truncateList(fileNameList, 80, "name") : "");

            // Full tape IDs for API payload: "VOLSER (item_id)" or "_ (item_id)"
            const fullList = relatedData.addedRows.map(item => {
                const volser = relatedData.dialogInitiator === 'itemList'
                    ? getFormattedProperty(item, "volsers", "str")
                    : getFormattedProperty(item, "item_volser", "str");
                const label = (volser && volser.trim()) ? volser.trim() : "_";
                return `${label} (${item.item_id})`;
            });
            setFullTapeIds(fullList.join(", "));

            // Preferences — new format (actionPreferences nested dict) or legacy format
            if (relatedData.actionPreferences) {
                // New path: OptionsDialog already produced the nested dict
                setActionPreferences(relatedData.actionPreferences);
                setConcatenatedOptions(stringifyPreferences(relatedData.actionPreferences));
            } else if (relatedData.checkoutRestorePreferences) {
                // Legacy path: convert old {key: {type, text, value}} format
                let prefs = {};
                Object.values(relatedData.checkoutRestorePreferences).forEach(detail => {
                    if (detail.value) {
                        (prefs[detail.type] ??= {})[detail.text] = true;
                    }
                });
                if (relatedData.cloudShow) prefs["Cloud Restore"] ??= {};
                if (relatedData.physicalShow) prefs["Physical Restore"] ??= {};
                setActionPreferences(prefs);
                setConcatenatedOptions(stringifyPreferences(prefs));
            }

            // Extract file details
            let localFileDetails = {};
            function _addFileDetails(volser, whole_item) {
                (localFileDetails[volser] ??= []).push({
                    "file_id": whole_item.file_id,
                    "path": whole_item.path,
                    "backup_source_data": {
                        "id": whole_item.backup_source_id,
                        "details": whole_item.backup_source_details,
                        "volsers": whole_item.volsers
                    },
                    "file_type": whole_item.file_type
                });
            }

            if (relatedData.dialogInitiator === 'itemList') relatedData.addedRows.forEach((addedItem) => {
                if (isString(addedItem?.volsers)) {
                    _addFileDetails(addedItem.volsers, addedItem);
                } else if (isArrayOfStrings(addedItem?.volsers)) {
                    addedItem.volsers.forEach(volser => _addFileDetails(volser, addedItem));
                } else {
                    console.error("Invalid volsers format", addedItem);
                }
            });
            setActionFileDetails(localFileDetails);
        }

        if (contactType === "destruction") {
            if (!relatedData) throw new Error("actionRelatedData is not defined for destruction");
            if (!relatedData.hasOwnProperty("addedRows")) {
                throw new Error("actionRelatedData must contain addedRows for destruction");
            }

            // Volsers from tapes — deduplicate only non-empty
            const volserList = relatedData.addedRows?.map((item) => getFormattedProperty(item, "item_volser", "str")) || [];
            const validVolsers = [...new Set(volserList.filter(v => v && v.trim()))];
            const emptyVolserCount = volserList.filter(v => !v || !v.trim()).length;
            const volsersWithEmpties = [...validVolsers, ...Array(emptyVolserCount).fill("")];
            setConcatenatedVolsers(truncateList(volsersWithEmpties, 80, "volser"));

            // Full tape IDs for API payload: "VOLSER (item_id)" or "_ (item_id)"
            const fullList = relatedData.addedRows.map(item => {
                const volser = getFormattedProperty(item, "item_volser", "str");
                const label = (volser && volser.trim()) ? volser.trim() : "_";
                return `${label} (${item.item_id})`;
            });
            setFullTapeIds(fullList.join(", "));

            // Preferences from OptionsDialog nested output
            if (relatedData.actionPreferences) {
                setActionPreferences(relatedData.actionPreferences);
                setConcatenatedOptions(stringifyPreferences(relatedData.actionPreferences));
            }
        }

    }, [relatedData]);

    // When confirmation becomes valid, trigger validation to surface any remaining errors
    useEffect(() => {
        if (contactType === "destruction" && isConfirmationValid) {
            trigger();
        }
    }, [isConfirmationValid]);

    useEffect(() => {
        if (contactType === "restore" || contactType === "destruction") {
            setCloseButtonText("Back");
        } else if (contactType === "help") {
            setCloseButtonText("Close");
        }
    }, [contactType]);

    const initializeForm = () => {
        reset(getDefaultValues());
        setIsDefaultLoaded(true);
    };

    useEffect(() => {
        initializeForm();
    }, [concatenatedFileNames, concatenatedVolsers, concatenatedOptions]);

    const useStyles = makeStyles({
        selectLabel: {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {borderRadius: "8px", borderColor: "#C7CCD0"},
                "&:hover fieldset": {borderColor: "#C7CCD0"},
                "&.Mui-focused fieldset": {borderColor: "#C7CCD0"},
            },
            "& .MuiInputBase-root.Mui-disabled": {
                "& > fieldset": {color: "rgba(255, 255, 255, 0.5)", borderColor: "#C7CCD0"}
            },
            "& .MuiOutlinedInput-input": {color: "#FFFFFF"},
            "& .MuiOutlinedInput-input.Mui-disabled": {WebkitTextFillColor: "rgba(255,255,255,0.5)"},
            "& .MuiInputBase-inputMultiline.Mui-disabled": {WebkitTextFillColor: "rgba(255,255,255,0.5)"},
            "& .MuiInputBase-inputMultiline": {color: "#FFFFFF"},
            "& .MuiSelect-root": {
                borderRadius: "8px",
                "& > fieldset": {color: "rgba(255, 255, 255, 0.5)"},
                "& .Mui-disabled": {color: "rgba(255, 255, 255, 0.5)"},
                "& .Mui-input": {color: "rgba(255, 255, 255, 0.5)"},
            },
            "& .MuiInputLabel-root": {
                color: "rgba(255, 255, 255, 0.5)",
                "&.Mui-focused": {color: "#FFC540"},
                "&.Mui-disabled": {color: "rgba(255, 255, 255, 0.5)"},
            },
            "& svg": {color: "#FFFFFF"},
        },
    });
    const classes = useStyles();

    // ─── Submit ───────────────────────────────────────────────────────────────
    const onSubmit = async (data) => {
        let payload = {
            company_name: data.companyName,
            contact_name: data.contactName,
            email: data.emailAddress,
            // Still phone_number on the way out: it is the column Contact has, and the request is one string
            // there whatever the form calls it. Renaming it would need a migration for a relabelled box.
            phone_number: data.mobileNumber,
            contact_type: contactType,
            message: data.additionalInstructions,
            urgency: data.urgencyLevel,
            method: data.options,
            contact_by_phone: true,
            contact_by_email: true,
            contact_email: data.emailAddress,
        };

        if (contactType === "restore") {
            payload = {
                ...payload,
                tape_ids: fullTapeIds,
                file_names: relatedData.dialogInitiator === 'itemList' ? data.fileNames : undefined,
                action_preferences: actionPreferences,
                action_file_details: relatedData.dialogInitiator === 'itemList' ? actionFileDetails : undefined,
            };
        }

        if (contactType === "destruction") {
            payload = {
                ...payload,
                tape_ids: fullTapeIds,
                action_preferences: actionPreferences,
            };
        }

        if (contactType === "help") {
            /*
             * The address only travels when something was typed into it.
             *
             * Reported as: "Even those fields are not mandatory throwing this error on submission." They are
             * not mandatory, and they were being sent anyway — as an address with every part blank, which
             * AddressSerializer refuses because an address it is given has to be an address. So a support
             * request with no address to give came back 400 on four fields the form does not even star, and
             * the operator got a raw DRF dump in a red banner for filling the form in correctly.
             *
             * related_address is required=False and allow_null=True on the serializer, so leaving it off is
             * the shape that means "no address" — which is what the empty block always meant.
             */
            if (isAddressStarted(name => data[name])) {
                payload = {
                    ...payload,
                    related_address: {
                        address_line_1: data.relatedAddress1,
                        address_line_2: "",
                        city: data.city,
                        state: data.state,
                        country: data.country,
                        postal_code: data.postalCode,
                    }
                };
            }
        }

        addContact(payload)
            .then(response => {
                openSnackbar("E-mail sent to Admin with your request.", "success");
                closeForm();
                reset(getDefaultValues());
                setConfirmationText("");
                onSuccessfulSubmit();
            })
            .catch(error => {
                console.error("Error sending a contact message", error);
                const message = error.response?.data?.detail || error.message || "An unknown error occurred while sending the request.";
                openSnackbar(message, "error");
            });
    };

    const getTitle = () => {
        switch (contactType) {
            case "restore": return "Restore Process";
            case "destruction": return "Destruction Request";
            case "help": return "Support Request";
            default: return "Contact Us";
        }
    };

    const renderDialogBackButton = () => {
        return <Grid item>
            <StyledButtonOutlined
                variant="outlined"
                onClick={() => {
                    closeForm();
                    reset(getDefaultValues());
                    setConfirmationText("");
                }}
                sx={{width: "200px", height: "60px"}}>
                {closeButtonText}
            </StyledButtonOutlined>
        </Grid>;
    };

    const renderDialogSubmitButton = () => {
        const isDestruction = contactType === "destruction";
        return <StyledButtonContained
            variant="contained"
            type="submit"
            disabled={!isValid || !isConfirmationValid || addressIncomplete}
            sx={{
                width: "200px",
                height: "60px",
                borderRadius: "30px",
                ...(isDestruction ? {
                    background: "#DD2A19",
                    color: "#FFFFFF",
                    "&:hover": {backgroundColor: "#B71C1C", color: "#FFFFFF"},
                    "&.Mui-disabled": {backgroundColor: "rgba(221,42,25,0.3)", color: "rgba(255,255,255,0.5)"},
                } : {}),
            }}>
            Submit
        </StyledButtonContained>;
    };

    const renderDialogBox = () => {
        return <Dialog
            open={isFormOpen}
            maxWidth="md"
            fullWidth={true}
            PaperProps={{sx: {borderRadius: "12px", backgroundColor: "#3A3E3F"}}}>
            <DialogContent sx={{p: 4, backgroundColor: "#3A3E3F", color: "#FFFFFF", ...disabledInputGreySx}}>
                <Box sx={{display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center"}}>
                    <Typography sx={{color: "#FFC540", fontSize: 18}}>
                        {getTitle()}
                    </Typography>
                </Box>
                <form id="ContactDialogForm" onSubmit={handleSubmit(onSubmit, errors => console.error(errors))}>
                    <Grid container spacing={2} style={{marginBottom: "12px"}}>
                        <ValidatedAdornedField
                            name="companyName"
                            label="Company Name"
                            register={register}
                            control={control}
                            classes={classes}
                            rules={{...companyNameValidationRules, required: "Company Name is required"}}
                            endAdornmentEnabled={true}
                            /*
                             * Locked only when the session knows the company. It is "" for a TapeArk admin —
                             * AuthProvider sets company_name from the customer, and an admin has none — so a
                             * disabled box was a required field nobody could ever fill and Submit stayed grey
                             * for good: the whole Support Request was unusable for admins. A customer user
                             * still cannot retype theirs, which is what the lock was for.
                             */
                            disabledStatus={Boolean(sessionCompanyName)}
                        />
                        <ValidatedAdornedField
                            name="contactName"
                            label="Contact Name"
                            register={register}
                            control={control}
                            classes={classes}
                            rules={{...contactNameValidationRules, required: "Contact Name is required"}}
                            endAdornmentEnabled={true}
                            disabledStatus={false}
                        />
                        <ValidatedAdornedField
                            name="emailAddress"
                            label="Email Address"
                            register={register}
                            control={control}
                            classes={classes}
                            rules={{...emailValidationRules, required: "Email Address is required"}}
                            endAdornmentEnabled={true}
                            disabledStatus={false}
                        />
                        {/*
                          * Mobile Number, as two boxes: the country code from a list, the digits beside it.
                          *
                          * Asked for as: "Change to this kind of standard and the support request should work
                          * and label should be Mobile number ... not phone number."
                          *
                          * It is also what makes the request go through. The old single box was checked against
                          * a pattern that only ever recognised +61 and +1, and the API stores this on a
                          * phonenumber_field, which refuses anything it cannot parse as a real international
                          * number — so a correctly written number from anywhere else was refused by the form,
                          * and a legacy local number with its trunk 0 was refused by the server after the send.
                          * A code that comes from a fixed list cannot be either.
                          */}
                        <ValidatedDialledNumberField
                            name="mobileNumber"
                            label="Mobile Number"
                            control={control}
                            classes={classes}
                            rules={{...nationalPhoneNumberValidationRules, required: "Mobile Number is required"}}
                            dataTestId="ContactForm-mobileNumber"
                        />
                        {contactType === "help" ?
                            <>
                                {/*
                                  * The address block: optional as a whole, all-or-nothing once begun.
                                  *
                                  * AddressSerializer requires every part of an address it is given, so a half
                                  * filled one is refused by the server with a message the operator sees in a
                                  * red banner attached to nothing. Asking for the rest here is the same rule,
                                  * stated where it can be answered. The stars appear only once something has
                                  * been typed into one of these five boxes.
                                  */}
                                <ValidatedAdornedField
                                    name="relatedAddress1"
                                    label="Related Address"
                                    register={register}
                                    control={control}
                                    classes={classes}
                                    rules={{...addressValidationRules, ...(addressStarted ? {required: "The address related to this request is required"} : {})}}
                                    endAdornmentEnabled={true}
                                    disabledStatus={false}
                                    xs={12}
                                />
                                <ValidatedAdornedField
                                    name="postalCode"
                                    label="Postal Code"
                                    register={register}
                                    control={control}
                                    classes={classes}
                                    rules={{...postalCodeValidationRules, ...(addressStarted ? {required: "The postal code related to this request is required"} : {})}}
                                    endAdornmentEnabled={true}
                                    disabledStatus={false}
                                    xs={3}
                                />
                                <ValidatedAdornedField
                                    name="city"
                                    label="City"
                                    register={register}
                                    control={control}
                                    classes={classes}
                                    rules={{...cityValidationRules, ...(addressStarted ? {required: "The city related to this request is required"} : {})}}
                                    endAdornmentEnabled={true}
                                    disabledStatus={false}
                                    xs={3}
                                />
                                <ValidatedAdornedField
                                    name="state"
                                    label="State"
                                    register={register}
                                    control={control}
                                    classes={classes}
                                    rules={{...stateValidationRules, ...(addressStarted ? {required: "The state related to this request is required"} : {})}}
                                    endAdornmentEnabled={true}
                                    disabledStatus={false}
                                    xs={3}
                                />
                                <ValidatedAdornedField
                                    name="country"
                                    label="Country"
                                    register={register}
                                    control={control}
                                    classes={classes}
                                    rules={{...countryValidationRules, ...(addressStarted ? {required: "The country related to this request is required"} : {})}}
                                    endAdornmentEnabled={true}
                                    disabledStatus={false}
                                    xs={3}
                                />
                            </> : <></>
                        }
                        {concatenatedFileNames && contactType === "restore" ?
                            <ValidatedAdornedField
                                name="fileNames"
                                label="File Names"
                                register={register}
                                control={control}
                                classes={classes}
                                rules={{}}
                                endAdornmentEnabled={false}
                                disabledStatus={true}
                                xs={12}
                            /> : <></>}
                        {(contactType === "restore" || contactType === "destruction") ?
                            <ValidatedAdornedField
                                name="tapeVolsers"
                                label="Tape Volsers"
                                register={register}
                                control={control}
                                classes={classes}
                                rules={{}}
                                endAdornmentEnabled={false}
                                disabledStatus={true}
                                xs={12}
                            /> : <></>}
                        <ValidatedAdornedField
                            name="additionalInstructions"
                            label="Additional Instructions"
                            register={register}
                            control={control}
                            classes={classes}
                            rules={{...descriptionMessageValidationRules(1200)}}
                            endAdornmentEnabled={true}
                            disabledStatus={false}
                            xs={12}
                            rows={4}
                        />
                        {(contactType === "restore" || contactType === "destruction") && concatenatedOptions ?
                            <ValidatedAdornedField
                                name="options"
                                label="Options"
                                register={register}
                                control={control}
                                classes={classes}
                                rules={{}}
                                endAdornmentEnabled={false}
                                disabledStatus={true}
                                xs={12}
                                rows={concatenatedOptions.split("\n").length}
                            /> : <></>}
                        <ValidatedAdornedField
                            name="urgencyLevel"
                            label="Urgency Level"
                            register={register}
                            control={control}
                            classes={classes}
                            rules={{...urgencyLevelValidationRules, required: "Urgency Level is required"}}
                            endAdornmentEnabled={true}
                            disabledStatus={false}
                            xs={12}
                            selectValueLabelMap={urgencyLevels}
                            trigger={trigger}
                        />
                        {contactType === "destruction" && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={`Type "${expectedConfirmation}" to confirm`}
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value)}
                                    className={classes.selectLabel}
                                    InputProps={{
                                        sx: {color: "#FFC540"},
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {borderColor: isConfirmationValid ? "#C7CCD0" : "#FFC540"},
                                            "&:hover fieldset": {borderColor: isConfirmationValid ? "#C7CCD0" : "#FFC540"},
                                            "&.Mui-focused fieldset": {borderColor: isConfirmationValid ? "#C7CCD0" : "#FFC540"},
                                        },
                                    }}
                                    helperText={!isConfirmationValid ? "* Type the exact phrase to confirm" : ""}
                                    FormHelperTextProps={{sx: {color: "#FFC540"}}}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Grid item>
                        <Grid container direction="row" justifyContent="space-between">
                            {renderDialogBackButton()}
                            {renderDialogSubmitButton()}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
        </Dialog>;
    };

    return (
        <>
            {isDefaultLoaded ? renderDialogBox() : <></>}
        </>
    );
};

export default ContactForm;
